import React, { useState, useRef } from 'react'
import { biaGetActivePrompt, biaListKnowledge } from '../lib/biaApi'

/* ============================================================================
   AgentTrace — trazabilidad IA (M4): muestra CON CERTEZA qué agente, qué prompt
   activo y qué conocimientos se usan para construir el script. El backend usa
   determinísticamente el prompt activo por agente + el knowledge del tag del
   agente, así que esto refleja exactamente lo que se inyecta.

   Solo admin/dev. Autocontenido (React + estilos inline, sin CoreUI).
   Props:
     agents      array de keys ['agent1_sst','agent3_gen',...] (opcional; default: los 4)
     defaultOpen bool
   ============================================================================ */

const AGENTS = [
  { key: 'agent1_sst', label: 'Agente 1 · Intérprete .sst', tag: 'sst' },
  { key: 'agent2_ssc', label: 'Agente 2 · Decompilador .ssc', tag: 'ssc' },
  { key: 'agent3_gen', label: 'Agente 3 · Generador data.json', tag: 'data_json' },
  { key: 'agent4_scriptlogic', label: 'Agente 4 · Handler / lógica', tag: 'logic' },
]

export default function AgentTrace({ agents = null, defaultOpen = false }) {
  const list = agents && agents.length ? AGENTS.filter((a) => agents.includes(a.key)) : AGENTS
  const [open, setOpen] = useState(defaultOpen)
  const [rows, setRows] = useState({}) // key -> { loading, prompt, knowledge, error }
  const loadedRef = useRef(false)

  function loadAll() {
    list.forEach(async (a) => {
      setRows((r) => ({ ...r, [a.key]: { loading: true } }))
      try {
        const [prompt, knowledge] = await Promise.all([
          biaGetActivePrompt(a.key).catch(() => null),
          biaListKnowledge({ active: true, agent: a.key, tag: a.tag, limit: 30 }).catch(() => []),
        ])
        setRows((r) => ({ ...r, [a.key]: { prompt, knowledge: Array.isArray(knowledge) ? knowledge : [] } }))
      } catch (e) {
        setRows((r) => ({ ...r, [a.key]: { error: e && e.message ? e.message : String(e) } }))
      }
    })
  }

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && !loadedRef.current) {
      loadedRef.current = true
      loadAll()
    }
  }

  function refresh() {
    loadedRef.current = true
    setRows({})
    loadAll()
  }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 12, background: '#f8fafc', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={toggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: 0, background: 'transparent', cursor: 'pointer', padding: '10px 14px', font: '600 13px system-ui', color: '#0f172a' }}
      >
        <span>🔎 Trazabilidad IA — agente · prompt activo · conocimientos</span>
        <span style={{ color: '#64748b', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open ? (
        <div style={{ padding: '0 14px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" onClick={refresh} style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>↻ Actualizar</button>
          </div>
          {list.map((a) => {
            const r = rows[a.key] || {}
            const p = r.prompt || {}
            const kn = r.knowledge || []
            return (
              <div key={a.key} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 10, padding: '9px 11px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 12.5, color: '#0f172a' }}>{a.label}</strong>
                  <code style={{ fontSize: 10.5, color: '#475569' }}>{a.key}</code>
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#64748b' }}>tag: <code>{a.tag}</code></span>
                </div>
                {r.loading ? (
                  <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 5 }}>Cargando…</div>
                ) : r.error ? (
                  <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 5 }}>Error: {r.error}</div>
                ) : (
                  <>
                    <div style={{ fontSize: 11.5, color: '#334155', marginTop: 5 }}>
                      <b>Prompt activo:</b>{' '}
                      {p && (p.id || p.version) ? (
                        <span>v{p.version ?? '?'} · id {p.id ?? '?'}{p.comment ? ' · ' + p.comment : ''}</span>
                      ) : (
                        <span style={{ color: '#b45309' }}>sin prompt activo en la DB</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#334155', marginTop: 3 }}>
                      <b>Conocimientos ({kn.length}):</b>{' '}
                      {kn.length ? (
                        <span style={{ color: '#475569' }}>{kn.slice(0, 12).map((k) => k.title || k.uuid).join(' · ')}{kn.length > 12 ? ` … (+${kn.length - 12})` : ''}</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>ninguno para este tag</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
            El backend inyecta el prompt activo del agente + los conocimientos de su tag. Esto es lo que se usa al construir.
          </div>
        </div>
      ) : null}
    </div>
  )
}
